import getCDNURL from '@pickleballinc/ui/components/global/utils/getCDNURL';
import { z } from 'zod';

import { CurrencyFormat } from '@/entities/CurrencyFormat';
import { TeamLeagueInfo } from '@/entities/TeamLeagueInfo';
import { TeamLeagueStoreType } from '@/store/team-league-store';

/**
 * Gets maxLength from a Zod schema field using multiple fallback methods
 */
export function getZodMaxLength(schema: z.ZodTypeAny, fieldName: string): number | undefined {
	if (fieldName.includes('.')) {
		try {
			const [arrayField, , field] = fieldName.split('.');
			if (!arrayField || !field) {
				return undefined;
			}
			// Get the array schema
			const arraySchema = (schema as any).shape?.[arrayField];
			// Get the element type schema (schema for each array item)
			const elementSchema = arraySchema?.element;
			// Now get maxLength from the field in the element schema
			return getZodMaxLength(elementSchema, field);
		} catch (_error) {
			// Continue to other methods if array handling fails
		}
	}

	// Method 1: Direct path (fastest)
	try {
		const result = (schema as any)._def.schema._def.schema.shape[fieldName]._def.innerType.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 2: Double nested innerType
	try {
		const result = (schema as any).shape?.[fieldName]?._def?.innerType?._def?.innerType?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 3: Direct from shape's _def (for simple string().max())
	try {
		const result = (schema as any).shape?.[fieldName]?._def?.checks?.find((check: any) => check.kind === 'max')?.value;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 4: For refined schemas
	try {
		const baseSchema = (schema as any)._def?.schema?._def?.schema;
		const result = baseSchema?.shape?.[fieldName]?._def?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 5: For optional/nullable fields
	try {
		const field = (schema as any)._def?.schema?._def?.schema?.shape?.[fieldName];
		const result = field?._def?.innerType?._def?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 6: Try through shape directly
	try {
		const result = (schema as any).shape?.[fieldName]?._def?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 7: From schema._def.schema._def.shape
	try {
		const result = (schema as any)._def.schema._def.shape[fieldName]._def.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 8: Check in checks array at different levels
	try {
		const checks = (schema as any)._def.schema._def.shape[fieldName]._def.checks;
		const result = checks?.find((check: any) => check.kind === 'max')?.value;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 9: Try getting from innerType directly
	try {
		const result = (schema as any)._def.schema._def.shape[fieldName]._def.innerType._def.checks?.find(
			(check: any) => check.kind === 'max'
		)?.value;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 10: For deeply nested schemas
	try {
		const result = (schema as any)._def.schema._def.shape[fieldName]._def.innerType._def.innerType._def.checks?.find(
			(check: any) => check.kind === 'max'
		)?.value;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 11: Most generic approach (last resort)
	try {
		let currentSchema = schema as any;
		while (currentSchema?._def?.schema) {
			currentSchema = currentSchema._def.schema;
		}
		const field = currentSchema?.shape?.[fieldName];
		let currentField = field;
		while (currentField?._def?.innerType) {
			currentField = currentField._def.innerType;
		}
		// Try getting from checks
		if (currentField?._def?.checks) {
			const maxCheck = currentField._def.checks.find((check: any) => check.kind === 'max');
			if (maxCheck?.value != null) return maxCheck.value;
		}
		// Try getting direct maxLength
		const result = currentField?._def?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// If all methods fail, return undefined
	return undefined;
}

export function pluralize(amount: number, word: string, suffix = 's'): string {
	return amount === 1 ? word : word + suffix;
}

export const suffixOptions = [
	{ value: 'I', label: 'I' },
	{ value: 'II', label: 'II' },
	{ value: 'III', label: 'III' },
	{ value: 'IV', label: 'IV' },
	{ value: 'Jr.', label: 'Jr.' },
	{ value: 'Sr.', label: 'Sr.' }
];

export const getProfileImageUrl = (profileImage: string | undefined, width?: number, height?: number): string => {
	if (profileImage) {
		const imageUrl = getCDNURL(profileImage, width || 500, height || 500);
		return imageUrl;
	} else {
		const defaultImageUrl = `${process.env.NEXT_PUBLIC_IMAGE_CDN}/pickleball-app/static/profile-image.svg`;
		return getCDNURL(defaultImageUrl, width || 500, height || 500);
	}
};

export const determinePriceDisplay = (price: number, currencyFormat?: CurrencyFormat): string => {
	if (!currencyFormat) {
		return price ? price.toFixed(2).toString() : '0.00';
	}
	if (currencyFormat.position === 'prefix') {
		return `${currencyFormat.symbol}${price ? price.toFixed(2).toString() : '0.00'}`;
	} else {
		return `${price ? price.toFixed(2).toString() : '0.00'}${currencyFormat.symbol}`;
	}
};

export const setTeamLeagueLabels = (store: TeamLeagueStoreType, response: TeamLeagueInfo) => {
	store.setLabels({
		tieBreakerMatchTitle: response.tieBreakerMatchTitle,
		tieBreakerMatchPluralTitle: response.tieBrekaerMatchPluralTitle,
		districtSingularTitle: response.districtSingularTitle,
		districtPluralTitle: response.districtPluralTitle,
		matchupSingularTitle: response.matchupSingularTitle,
		matchupPluralTitle: response.matchupPluralTitle
	});
};

export const isEmptyObject = (obj: object): boolean => {
	return Object.keys(obj).length === 0;
};
